import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useChat } from "../../context/ChatContext";
import { useCart } from "../../context/CartContext";
import { api, formatApiError } from "../../lib/api";
import { X, Send, MessageCircle, Loader2, Sparkles, RefreshCw } from "lucide-react";
import { useTheme } from "../../context/ThemeContext";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/wingman/7b8b7474-e52e-4388-adc0-a2361520c0a4/attachments/8840390a845949e6b9092405676c1743_image.png";

const INITIAL_GREETING = {
  role: "assistant",
  content:
    "Ciao 👋 Sono **Leo**, l'assistente virtuale di LEOVERA basato su intelligenza artificiale. " +
    "Ti aiuto a scegliere i prodotti e a completare l'ordine. Le mie risposte sono indicative e " +
    "non sostituiscono il parere di un medico o di un nutrizionista.",
};

const ChatWidget = () => {
  const location = useLocation();
  const { open, setOpen, sessionId, setSessionId, consumePrefill, resetSession } = useChat();
  const { clear: clearCart, items: cartItems } = useCart();
  const { settings } = useTheme();
  const assistantName = settings?.assistant_name || "Leo";
  const chatButtonLabel = settings?.chat_button_label || "PARLA CON LEO";
  const [messages, setMessages] = useState([INITIAL_GREETING]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [orderConfirmed, setOrderConfirmed] = useState(null);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  // Load existing history when widget opens
  useEffect(() => {
    if (!open) return;
    inputRef.current?.focus();
    // Inject any prefill message
    const pref = consumePrefill();
    if (pref) setInput((cur) => cur || pref);
    if (sessionId) {
      (async () => {
        try {
          const { data } = await api.get(`/chat/history/${sessionId}`, { timeout: 30000 });
          if (data?.messages?.length) {
            setMessages([INITIAL_GREETING, ...data.messages.map((m) => ({
              role: m.role, content: m.content,
            }))]);
          }
        } catch { /* keep greeting */ }
      })();
    }
  }, [open, sessionId, consumePrefill]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  const send = async (text) => {
    const t = (text ?? input).trim();
    if (!t || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: t }]);
    setSending(true);
    try {
      const payload = { message: t };
      if (sessionId) payload.session_id = sessionId;
      const { data } = await api.post("/chat/message", payload, { timeout: 45000 });
      if (!sessionId && data.session_id) setSessionId(data.session_id);
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
      if (data.order_created) {
        setOrderConfirmed({
          order_number: data.order_number,
          total: data.order_total,
        });
        clearCart();
      }
    } catch (e) {
      setMessages((m) => [...m, {
        role: "assistant",
        content: `⚠️ Errore di rete: ${formatApiError(e)}. Riprova tra qualche secondo.`,
      }]);
    } finally {
      setSending(false);
    }
  };

  const startNew = () => {
    resetSession();
    setMessages([INITIAL_GREETING]);
    setOrderConfirmed(null);
  };

  const cartSummary = cartItems.length > 0
    ? `Ho ${cartItems.length} ${cartItems.length === 1 ? "prodotto" : "prodotti"} nel carrello (${cartItems.map(i => `${i.quantity}× ${i.name}`).join(", ")}). Aiutami a finalizzare l'ordine.`
    : null;

  // Public-only widget: never render inside the admin dashboard.
  if (location.pathname.startsWith("/admin")) return null;

  return (
    <>
      {/* Floating launcher — hidden on /checkout: on phones it sat on top of
          "Conferma e paga", so tapping pay opened the chat instead. */}
      {!open && location.pathname !== "/checkout" && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          data-testid="chat-launcher"
          aria-label="Apri chat con l'assistente"
          className="fixed bottom-5 right-5 z-40 inline-flex items-center gap-3 bg-[#0A1121] border border-[#C0C0C0]/40 hover:border-[#C0C0C0] text-white px-5 py-3.5 rounded-full shadow-[0_10px_40px_-10px_rgba(0,0,0,0.6)] transition-all hover:translate-y-[-2px]"
        >
          <span className="relative inline-flex">
            <MessageCircle className="w-5 h-5 text-[#C0C0C0]" strokeWidth={1.5} />
            <span className="absolute -top-1 -right-1 w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
          </span>
          <span className="text-[11px] tracking-[0.24em] uppercase">{chatButtonLabel}</span>
        </button>
      )}

      {open && (
        <div
          data-testid="chat-widget"
          className="fixed inset-x-3 bottom-3 sm:inset-auto sm:bottom-5 sm:right-5 sm:w-[420px] z-50 bg-[#0A1121] border border-white/10 rounded-xl shadow-[0_25px_80px_-20px_rgba(0,0,0,0.8)] flex flex-col max-h-[88vh] overflow-hidden"
        >
          {/* Header */}
          <header className="px-4 py-3.5 border-b border-white/10 flex items-center justify-between gap-3 bg-[#060A14]">
            <div className="flex items-center gap-3 min-w-0">
              <img src={LOGO_URL} alt="LEOVERA" className="w-9 h-9 rounded-md bg-white p-1 object-contain shrink-0" />
              <div className="flex flex-col min-w-0">
                <span className="font-[Outfit] text-white text-[14px] tracking-[0.14em]">{assistantName.toUpperCase()}</span>
                <span className="text-[9px] tracking-[0.2em] uppercase text-[#94A3B8]" data-testid="chat-ai-disclaimer-label">
                  Assistente AI
                </span>
                <span className="text-[10px] tracking-[0.24em] uppercase text-emerald-300 flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full" /> Online · risponde in italiano
                </span>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={startNew} data-testid="chat-reset" aria-label="Nuova conversazione"
                      className="p-2 text-[#94A3B8] hover:text-white">
                <RefreshCw className="w-4 h-4" />
              </button>
              <button onClick={() => setOpen(false)} data-testid="chat-close" aria-label="Chiudi chat"
                      className="p-2 text-[#94A3B8] hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
          </header>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-gradient-to-b from-[#0A1121] to-[#060A14]">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  data-testid={`chat-msg-${m.role}-${i}`}
                  className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-[13px] leading-relaxed whitespace-pre-wrap ${
                    m.role === "user"
                      ? "bg-[#C0C0C0] text-[#060A14] rounded-br-sm"
                      : "bg-white/5 border border-white/10 text-[#E5E4E2] rounded-bl-sm"
                  }`}
                >
                  {m.content}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start">
                <div className="px-4 py-2.5 rounded-2xl rounded-bl-sm bg-white/5 border border-white/10 text-[#94A3B8] text-[13px] inline-flex items-center gap-2">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  {assistantName} sta scrivendo…
                </div>
              </div>
            )}
            {orderConfirmed && (
              <div data-testid="chat-order-confirmed" className="my-2 px-4 py-3 rounded-lg bg-emerald-500/10 border border-emerald-500/40 text-emerald-200 text-[12px]">
                ✅ Ordine <strong>{orderConfirmed.order_number}</strong> registrato — totale €{Number(orderConfirmed.total).toFixed(2)}.
                Sarai contattato da LEOVERA per la conferma definitiva.
              </div>
            )}
          </div>

          {/* Quick chips */}
          {cartSummary && messages.length <= 2 && (
            <div className="px-4 py-2 border-t border-white/10 bg-[#060A14]">
              <button
                type="button"
                onClick={() => send(cartSummary)}
                data-testid="chat-cart-suggestion"
                className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-full text-[10px] tracking-[0.2em] uppercase border border-[#C0C0C0]/30 text-[#E5E4E2] hover:bg-[#C0C0C0]/10"
              >
                <Sparkles className="w-3 h-3" /> Finalizza il carrello con {assistantName}
              </button>
            </div>
          )}

          {/* Composer */}
          <form
            onSubmit={(e) => { e.preventDefault(); send(); }}
            className="px-3 py-3 border-t border-white/10 bg-[#060A14] flex items-end gap-2"
            data-testid="chat-composer"
          >
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
              }}
              placeholder="Scrivi un messaggio…"
              rows={1}
              disabled={sending}
              data-testid="chat-input"
              className="flex-1 resize-none bg-[#0A1121] border border-white/10 rounded-lg px-3 py-2.5 text-white text-[13px] leading-snug focus:outline-none focus:border-[#C0C0C0] max-h-32"
            />
            <button type="submit" disabled={!input.trim() || sending} data-testid="chat-send"
                    className="shrink-0 inline-flex items-center justify-center w-10 h-10 rounded-lg bg-[#C0C0C0] text-[#060A14] hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </button>
          </form>
        </div>
      )}
    </>
  );
};

export default ChatWidget;
